import { FeatureGroup, useMap } from "react-leaflet";
import {
  leafletLayer,
  PolygonSymbolizer,
  type LabelRule,
  type PaintRule,
} from "protomaps-leaflet";
import { useEffect, useRef } from "react";
import { type GridLayer } from "leaflet";
import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";
import {
  TRUSTART_CHIBAN_COVERAGE_FILL_COLOR,
  TRUSTART_CHIBAN_COVERAGE_FILL_OPACITY,
  TRUSTART_CHIBAN_COVERAGE_STROKE_COLOR,
  TRUSTART_CHIBAN_COVERAGE_STROKE_OPACITY,
  TRUSTART_CHIBAN_PARCEL_STROKE_COLOR,
} from "@/features/map/constants/trustartChibanLayer";
import { ParcelLabelSymbolizer } from "@/features/map/utils/parcelLabelSymbolizer";
import { TrustartChibanCoverageStrokeOverlay } from "@/features/map/utils/trustartChibanCoverageStrokeOverlay";

// レイヤーパネル・排他制御で使う表示名（TRUSTART 地番図データの製品表記は「役所地番地図」）
export const TRUSTART_CHIBAN_LAYER_NAME = "役所地番地図";

// pmtiles 内部のレイヤー名（tippecanoe の -L で作るレイヤーと一致させる）。
// parcels=筆界ポリゴン（枠線）、parcel_labels=地番ラベル用の代表点、
// coverage_areas=対応エリアポリゴン（筆界の表示最小ズーム未満で対応範囲を示す）。
const TRUSTART_CHIBAN_DATA_LAYER_NAME = "parcels";
const TRUSTART_CHIBAN_LABEL_LAYER_NAME = "parcel_labels";
const TRUSTART_CHIBAN_COVERAGE_LAYER_NAME = "coverage_areas";

// 枠線の表示ズーム（タイル生成は -Z14、表示は 14 から。ただし protomaps は
// levelDiff=1 のため実際に筆界が描けるのは表示ズーム 15 から）
const POLYGON_MIN_ZOOM = 14;
const POLYGON_MAX_ZOOM = 22;

// 地番ラベルの表示ズーム（密集を避けるため 16 から）
const LABEL_MIN_ZOOM = 16;
const LABEL_MAX_ZOOM = 22;

// タイルデータの最大ズーム（tippecanoe の -z16 と一致。これ以上はオーバーズーム）
const MAX_DATA_ZOOM = 16;

// 筆界データの最小ズーム（tippecanoe の -Z14 と一致）。protomaps-leaflet は
// levelDiff=1（表示ズーム z は z-1 のデータタイルで描画）のため、筆界が実際に
// 表示できるのは表示ズーム MIN_DATA_ZOOM+1 から。表示ズーム MIN_DATA_ZOOM では
// データが存在せず筆界を描けないので、そのズームまでは対応エリアを表示する。
const MIN_DATA_ZOOM = 14;

// 対応エリアの表示ズーム（表示ズーム基準・両端 inclusive）。
// 筆界がデータ都合で描けない表示ズーム帯（〜MIN_DATA_ZOOM）までをカバーする
const COVERAGE_MIN_ZOOM = 0;
const COVERAGE_MAX_ZOOM = MIN_DATA_ZOOM;

// 対応エリアの枠線の表示最小ズーム。z10 以下の広域では境界線が細かすぎて
// ノイズになるため塗りのみ表示し、枠線は z11 から描く
const COVERAGE_STROKE_MIN_ZOOM = 11;

// 筆界の線幅
const POLYGON_WIDTH = 1;

// 対応エリアの枠線の線幅
const COVERAGE_STROKE_WIDTH = 1;

const ATTRIBUTION = "地番図データ © TRUSTART";

interface Props {
  /** 外部から制御される表示状態 */
  visible: boolean;
  /** 統合タイル（pmtiles）の配信 URL */
  pmtilesUrl: string;
}

/**
 * 役所地番地図（TRUSTART 地番図データ）のタイルレイヤー。
 *
 * 全対応自治体を統合した 1 つの pmtiles を描画する（自治体ごとのレイヤー分割・bbox
 * 処理は廃止）。pmtiles 内の parcels レイヤーで筆界の枠線（z15+）を、parcel_labels
 * レイヤー（筆ごとの代表点）で地番ラベル（z16+）を描画する。ラベルを代表点に付ける
 * ことでタイル境界をまたぐ筆でもラベルが二重表示されない。筆界が表示されない低ズーム帯
 * （表示 z14 以下）では coverage_areas レイヤーで対応エリアのポリゴンを描画し、
 * 対応範囲を俯瞰できるようにする。表示/非表示は visible prop で制御し、再生成を
 * 避けるため URL 変更時のみレイヤーを作り直す（SchoolAreaLayer に倣う）。
 *
 * 描画は3層構成:
 * 1. 対応エリアの塗り（GridLayer）: canvas に不透明で描き、コンテナの CSS opacity で
 *    透過する。半透明で直接描くとズーム遷移中に新旧タイルの重なりでアルファが加算
 *    されて濃く見えるため。
 * 2. 対応エリアの枠線（SVG ベクタ）: canvas 描画ではズーム遷移中に太さが脈打つ／
 *    消えるのが避けられないため（詳細は TrustartChibanCoverageStrokeOverlay 参照）。
 * 3. 筆界＋地番ラベル（GridLayer）。
 */
export const TrustartChibanTileLayer: React.FC<Props> = ({
  visible,
  pmtilesUrl,
}) => {
  const map = useMap();
  const layerRef = useRef<GridLayer | null>(null);
  const coverageFillLayerRef = useRef<GridLayer | null>(null);
  const coverageStrokeOverlayRef =
    useRef<TrustartChibanCoverageStrokeOverlay | null>(null);

  // URL が決まったらレイヤーを生成する（地図への追加/削除は別 useEffect が担当するため
  // ここでは visible を参照しない＝表示切り替えでレイヤーが再生成されない）
  useEffect(() => {
    if (!map || pmtilesUrl === "") return;

    // 対応エリアの塗り。不透明で描き、透過はコンテナの CSS opacity で一括適用する
    const coverageFillRule: PaintRule = {
      dataLayer: TRUSTART_CHIBAN_COVERAGE_LAYER_NAME,
      symbolizer: new PolygonSymbolizer({
        fill: TRUSTART_CHIBAN_COVERAGE_FILL_COLOR,
      }),
      minzoom: COVERAGE_MIN_ZOOM,
      maxzoom: COVERAGE_MAX_ZOOM,
    };

    const coverageFillLayer = leafletLayer({
      url: pmtilesUrl,
      paintRules: [coverageFillRule],
      labelRules: [],
      maxDataZoom: MAX_DATA_ZOOM,
      // 本体と同一文字列にする（Leaflet は同一の帰属表示を重複させない。
      // 省略すると protomaps-leaflet 既定の "Protomaps © OpenStreetMap" が出てしまう）
      attribution: ATTRIBUTION,
    }) as GridLayer;
    // 本体レイヤーと同じ zIndex にし、本体より先に addTo することで（同値 zIndex は
    // DOM 順で決まる）塗りが枠線・ラベルの下になる
    coverageFillLayer.setZIndex(LAYER_Z_INDEX.TRUSTART_CHIBAN);
    coverageFillLayer.setOpacity(TRUSTART_CHIBAN_COVERAGE_FILL_OPACITY);
    coverageFillLayerRef.current = coverageFillLayer;

    // 対応エリアの枠線（SVG ベクタ）
    const coverageStrokeOverlay = new TrustartChibanCoverageStrokeOverlay(
      map,
      pmtilesUrl,
      TRUSTART_CHIBAN_COVERAGE_LAYER_NAME,
      {
        color: TRUSTART_CHIBAN_COVERAGE_STROKE_COLOR,
        weight: COVERAGE_STROKE_WIDTH,
        opacity: TRUSTART_CHIBAN_COVERAGE_STROKE_OPACITY,
        minZoom: COVERAGE_STROKE_MIN_ZOOM,
        maxZoom: COVERAGE_MAX_ZOOM,
      }
    );
    coverageStrokeOverlayRef.current = coverageStrokeOverlay;

    const paintRule: PaintRule = {
      dataLayer: TRUSTART_CHIBAN_DATA_LAYER_NAME,
      symbolizer: new PolygonSymbolizer({
        fill: "transparent",
        stroke: TRUSTART_CHIBAN_PARCEL_STROKE_COLOR,
        width: POLYGON_WIDTH,
      }),
      minzoom: POLYGON_MIN_ZOOM,
      maxzoom: POLYGON_MAX_ZOOM,
    };

    const labelRule: LabelRule = {
      dataLayer: TRUSTART_CHIBAN_LABEL_LAYER_NAME,
      symbolizer: new ParcelLabelSymbolizer({
        textGetter: (props) =>
          props.chiban === undefined || props.chiban === null
            ? ""
            : String(props.chiban),
      }),
      minzoom: LABEL_MIN_ZOOM,
      maxzoom: LABEL_MAX_ZOOM,
    };

    const layer = leafletLayer({
      url: pmtilesUrl,
      paintRules: [paintRule],
      labelRules: [labelRule],
      maxDataZoom: MAX_DATA_ZOOM,
      attribution: ATTRIBUTION,
    }) as GridLayer;

    layer.setZIndex(LAYER_Z_INDEX.TRUSTART_CHIBAN);
    layerRef.current = layer;

    return () => {
      if (map && layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      if (map && coverageFillLayerRef.current) {
        map.removeLayer(coverageFillLayerRef.current);
        coverageFillLayerRef.current = null;
      }
      coverageStrokeOverlayRef.current?.remove();
      coverageStrokeOverlayRef.current = null;
    };
  }, [map, pmtilesUrl]);

  // visible の変化、または pmtilesUrl 変更によるレイヤー再生成に応じて地図への追加/削除を切り替える
  // （pmtilesUrl を依存に含めるのは、上の useEffect が作り直した新レイヤーを再追加するため）
  useEffect(() => {
    const layer = layerRef.current;
    const fillLayer = coverageFillLayerRef.current;
    const strokeOverlay = coverageStrokeOverlayRef.current;
    if (!layer) return;

    // 塗り→本体の順で追加し、同一 zIndex 内（DOM 順）で対応エリアの塗りが
    // 筆界・ラベルの下になるようにする（枠線は専用ペインで zIndex 制御）
    if (visible) {
      if (fillLayer && !map.hasLayer(fillLayer)) {
        fillLayer.addTo(map);
      }
      if (!map.hasLayer(layer)) {
        layer.addTo(map);
      }
      strokeOverlay?.add();
    } else {
      if (fillLayer && map.hasLayer(fillLayer)) {
        map.removeLayer(fillLayer);
      }
      if (map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
      strokeOverlay?.remove();
    }
  }, [visible, map, pmtilesUrl]);

  return <FeatureGroup />;
};
